import { TOrganization, TOrganizationAdminListItem, TOrganizationSuperAdminExtended } from '@/types/Organization.types';
import { api, TCustomAxiosPaginationResponse } from '..';
import { TAdminOrganizationsSortingColumn } from '@/routes/_authenticated/_admin/organizations';
import { ITEMS_PER_PAGE } from '@/theme/components/table';
import { TUser } from '@/types/User.types';
import { apiPaths } from '@/services/api/paths';
import { SUPER_ADMIN_PAGE_SIZE } from '@/constants/numberVars';

export const getAllOrganizations = async (
  page: number,
  filterParams?: { searchText: string },
  sortingColumn?: string,
  sortingDirection?: string,
): Promise<TCustomAxiosPaginationResponse<TOrganizationAdminListItem[]>> => {
  let sortBy = '';
  switch (sortingColumn as TAdminOrganizationsSortingColumn) {
    case 'Representative Email':
      sortBy = 'representativeEmail';
      break;
    case 'Total # of Employees':
      sortBy = 'totalUsersCount';
      break;
    case '# of Onboarded':
      sortBy = 'onboardedUsersCount';
      break;
    case '# of Member Cares':
      sortBy = 'assignedMemberCaresCount';
      break;

    case 'Name':
    default:
      sortBy = 'name';
      break;
  }

  const params: { limit: number; page: number; sortBy: string; order: string | undefined; search?: string } = {
    limit: SUPER_ADMIN_PAGE_SIZE,
    page,
    sortBy,
    order: sortingDirection,
  };

  if (filterParams?.searchText) params.search = filterParams?.searchText;

  const { data } = await api.get(apiPaths.organization.root, {
    params,
  });

  return data;
};

export const getOrganizationRoster = async (
  page: number,
  activeUsers: boolean,
  filterParams?: { searchText: string },
  additionalParams?: { id: string },
): Promise<TCustomAxiosPaginationResponse<TUser[]>> => {
  const params: { limit: number; page: number; search?: string } = {
    limit: ITEMS_PER_PAGE,
    page,
  };

  if (filterParams?.searchText) params.search = filterParams?.searchText;

  const { data } = await api.get<TCustomAxiosPaginationResponse<TUser[]>>(
    apiPaths.organization.users(String(additionalParams?.id)),
    {
      params: {
        ...params,
        activeUsers,
      },
    },
  );

  return data;
};


export const organizationByIdGet = async ({ id }: { id: string }): Promise<TOrganizationSuperAdminExtended> => {
  const { data } = await api.get(apiPaths.organization.byId(id));

  const organization: TOrganizationSuperAdminExtended = data.data;

  return organization;
};

export const createOrganizationPost = async ({ name }: { name: string }): Promise<TOrganization> => {
  const { data } = await api.post(apiPaths.organization.root, {
    name,
  });

  const organization = data.data;

  return organization;
};

export const updateOrganizationByIdPut = async (organizationId: string, orgInfo: TOrganization) => {
  const { data } = await api.put<TCustomAxiosPaginationResponse<TOrganization>>(
    apiPaths.organization.byId(organizationId),
    {
      ...orgInfo,
    },
  );
  const updatedOrganization = data.data;
  return updatedOrganization;
};

export const inviteUsersToOrganizationPost = async (
  users: { email: string; phone: string; firstName: string; lastName: string }[],
) => {
  const { data } = await api.post<TCustomAxiosPaginationResponse<unknown>>(apiPaths.organization.inviteUsers, {
    users,
  });

  return data.data;
};

export const reinviteUserPost = async (userId: string, organizationId: string) => {
  const data = await api.post(apiPaths.organization.reinvite(organizationId, userId));
  return data;
};

export const inviteOrganizationAdminPost = async ({
  organizationId,
  firstName,
  lastName,
  email,
}: {
  organizationId: string;
  firstName: string;
  lastName: string;
  email: string;
}): Promise<string> => {
  const { data } = await api.post(apiPaths.organization.inviteAdmin(organizationId), {
    firstName,
    lastName,
    email,
  });

  const message: string = data.data;

  return message;
};

export const postChangeOrganizationLogo = async (organizationId: string, newProfilePhoto: File): Promise<string> => {
  const formData = new FormData();

  if (newProfilePhoto) {
    formData.append('file', new File([newProfilePhoto], newProfilePhoto.name));
  }

  const { data } = await api.post(apiPaths.organization.uploadLogo(organizationId), formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  const resultData = data.data;

  return resultData;
};

export const patchOrganizationActivation = async (organizationId: string, action: 'deactivate' | 'reactivate') => {
  const path =
    action === 'deactivate'
      ? apiPaths.organization.deactivate(organizationId)
      : apiPaths.organization.reactivate(organizationId);
  const { data } = await api.patch(path, {});
  return data;
};
